/**
 * DecoderSigil — the "standard little something."
 *
 * The ASCII decoder / slot-machine sigil, extracted from the connect->play
 * storyboard (frontend/web/preview/login-sequence/index.html mountSigil()) into
 * a reusable component for the shipped shell.
 *
 * Visual canon (docs/Tome/_meta/ui-visual-canon.md §3):
 *   - CLOCKWORK, not eased: a fixed ~80ms tick, mechanical; no bounce/ease.
 *   - Evolve in the grid: a 15-cell reel expands between <| |>, each cell churns
 *     through glyphs, then locks LEFT->RIGHT into " R E A V E R S ".
 *   - Color is the emphasis: churning cells dim, locked letters bright amber,
 *     a unified RED flash on the resolved word, then it vanishes and loops.
 *   - On `admitted`/warm it resolves to the static word in GREEN (no animation).
 *
 * The animation reads from CSS classes on the .sigil element (see shell.css),
 * so all palette/timing stays in the shared visual layer. This module owns only
 * the clockwork sequencing.
 */

const TARGET = [' ', 'R', ' ', 'E', ' ', 'A', ' ', 'V', ' ', 'E', ' ', 'R', ' ', 'S', ' ']; // 15 cells
const GLYPHS = '-]%@#/\\*+=~:<>';

const rnd = () => GLYPHS[(Math.random() * GLYPHS.length) | 0];
const cell = (ch, cls) => `<span class="sig-cell ${cls}">${ch === ' ' ? '&nbsp;' : ch}</span>`;

export class DecoderSigil {
    constructor() {
        this._timer = null;
        this._el = null;
        this._reels = null;
        this._warm = false;
        // clockwork timeline (all in ticks) — mirrors the storyboard, tunable here
        this.TICK = 80;
        this.EXPAND = 15;
        this.LOCK_EVERY = 2;
        this.HOLD = 6;
        this.FLASH = 3;
        this.GAP = 7;
    }

    /**
     * Build the sigil DOM (<| <reels> |>) inside `host` and start the clockwork.
     * Returns the .sigil element.
     */
    mount(host, { warm = false } = {}) {
        this.stop();
        const sigil = document.createElement('div');
        sigil.className = 'sigil' + (warm ? ' warm' : '');
        sigil.innerHTML =
            '<span class="sig-l">&lt;|</span>' +
            '<span class="sig-reels"></span>' +
            '<span class="sig-r">|&gt;</span>';
        host.appendChild(sigil);
        this._el = sigil;
        this._reels = sigil.querySelector('.sig-reels');
        // Normalised to a boolean so `setWarm`'s no-change guard compares like
        // with like: a truthy non-boolean here (a 1, a string) would never
        // equal the `!!warm` the guard computes, and every caller would restart
        // the clockwork it is meant to protect.
        this._warm = !!warm;
        this.start();
        return sigil;
    }

    /**
     * Flip to/from the warm (admitted) resolved-green static word.
     *
     * ⚠️ A NO-OP WHEN THE STATE HAS NOT CHANGED, AND THAT GUARD IS THE WHOLE
     * POINT OF THIS METHOD'S EXISTENCE.
     *
     * `start()` restarts the clockwork at `t = 0`. `Shell._setReadBoxStatus`
     * calls this on EVERY `queue_position` frame — and while a player is
     * queued, `warm` is false on every one of them. So an unguarded flip
     * restarted the reel on the polling cadence: the sigil would get part-way
     * through its left-to-right lock and snap back to a single cell, over and
     * over. Reported from the queue screen as "<| R E / + # ] * |>" — which is
     * exactly a half-locked frame being truncated mid-animation.
     *
     * THE ANIMATION IS CLOCKWORK AND OWNS ITS OWN TIMELINE (visual canon §3:
     * "CLOCKWORK, not eased: a fixed ~80ms tick, mechanical"). Nothing on the
     * network cadence — a queue poll, a beat, a TTL — may reach in and reset
     * it. A status line changing above the sigil is not a reason to restart the
     * sigil.
     */
    setWarm(warm) {
        if (!this._el) return;
        const next = !!warm;
        if (next === this._warm) return;   // no change -> let the clockwork run
        this._warm = next;
        this._el.classList.toggle('warm', next);
        this.start();
    }

    start() {
        this.stop();
        if (!this._el || !this._reels) return;

        // warm/admitted -> resolved word, static green (no animation)
        if (this._warm) {
            this._reels.innerHTML = TARGET.map((c) => cell(c, 'lock')).join('');
            return;
        }

        const N = TARGET.length;
        const tExpand = this.EXPAND;
        const tLock = tExpand + N * this.LOCK_EVERY;
        const tHold = tLock + this.HOLD;
        const tFlash = tHold + this.FLASH;
        const tEnd = tFlash + this.GAP;
        let t = 0;

        const frame = () => {
            const sig = this._el;
            if (!sig) return;
            sig.classList.remove('flash', 'gone');
            let html = '';
            if (t < tExpand) {
                for (let i = 0; i <= t; i++) html += cell(rnd(), 'spin');
            } else if (t < tLock) {
                const ptr = ((t - tExpand) / this.LOCK_EVERY) | 0;
                for (let i = 0; i < N; i++) {
                    html += (i <= ptr) ? cell(TARGET[i], 'lock') : cell(rnd(), 'spin');
                }
            } else if (t < tFlash) {
                for (let i = 0; i < N; i++) html += cell(TARGET[i], 'lock');
                if (t >= tHold) sig.classList.add('flash');
            } else {
                for (let i = 0; i < N; i++) html += cell(TARGET[i], 'lock');
                sig.classList.add('gone');
            }
            this._reels.innerHTML = html;
            t = (t + 1) % tEnd;
        };

        frame();
        this._timer = setInterval(frame, this.TICK);
    }

    stop() {
        if (this._timer) {
            clearInterval(this._timer);
            this._timer = null;
        }
    }

    destroy() {
        this.stop();
        if (this._el && this._el.parentNode) {
            this._el.parentNode.removeChild(this._el);
        }
        this._el = null;
        this._reels = null;
    }
}

export default DecoderSigil;
